// SGNL-218 — Lost-deal intelligence helpers.
//
// Pure functions consumed by the Lost-deal-analysis card on the
// opp-detail page (`views/opp_detail.ts`). All data comes from the
// detail payload's `history` array plus the global bundle's
// `pre_close_max_stage` distribution — no new BE endpoints.
//
// The helpers are kept in `lib/` (not `views/`) so the same
// computations can be reused by the SGNL-216 multi-deal aggregate
// analysis follow-up if/when that ships.

import type { OpportunityHistoryEntry, OpportunityRow } from "../types";

// ---------------------------------------------------------------------------
// Stage timeline
// ---------------------------------------------------------------------------

/** Stages SF uses for terminal closure — excluded from the journey. */
const TERMINAL_STAGES = new Set(["Closed Won", "Closed Lost"]);

export interface StageTimelineEntry {
  /** Stage name as it appeared in opportunity_history (BTRIM'd). */
  stage: string;
  /** Date the opp entered this stage (ISO 8601 from the history row's
   *  `created_date`). null if the upstream row's date was malformed. */
  enteredAt: string | null;
  /** Days the opp held this stage. Null for the most recent entry
   *  (no subsequent entry to compute against — or the terminal stage). */
  daysInStage: number | null;
  /** Probability % at the time of this history entry. */
  probability: number | null;
  /** Amount on this history entry (capped by BE at $50M for sanity). */
  amount: number | null;
}

function parseIsoDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

const _MS_PER_DAY = 86_400_000;

/** Calendar-day difference between two Dates, normalised to UTC midnight
 *  on both sides. PR-Agent #137 imp 6 — raw `(b - a) / 86_400_000` +
 *  Math.round produced off-by-one results when timestamps weren't
 *  midnight (e.g. a Date at 2026-03-15T23:30:00Z and another at
 *  2026-03-16T00:30:00Z would round to 0 days even though the calendar
 *  changed). Normalising to UTC midnight first makes "days" mean
 *  calendar-day difference and chip triggering stable. */
function daysBetween(a: Date, b: Date): number {
  const aUTC = Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), a.getUTCDate());
  const bUTC = Date.UTC(b.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate());
  return Math.round((bUTC - aUTC) / _MS_PER_DAY);
}

// SGNL-218 pre-PR review — named thresholds, exported so tests + the
// view share one canonical source. Searchable; tuneable in one place.
/** Stall-threshold: a non-terminal stage held for more than this many
 *  days surfaces as a "stalled-in-stage" chip. */
export const STALLED_THRESHOLD_DAYS = 60;
/** Abrupt-loss window: peak probability landed within this many days
 *  of close → "abrupt loss" chip. */
export const ABRUPT_THRESHOLD_DAYS = 7;
/** Floor on peak probability for the abrupt-loss chip — sub-qualification
 *  history entries don't qualify as "looked like a win". */
export const ABRUPT_PROB_FLOOR = 70;

/** Build the stage journey table from opportunity_history.
 *
 *  Skips terminal stages ("Closed Won" / "Closed Lost") — they're noise
 *  for the "what stages did this deal travel through" narrative.
 *  Empty/whitespace stages are dropped too (some SF history rows ship
 *  blank).
 *
 *  Sorted oldest → newest. `daysInStage` is computed as the gap until
 *  the next non-terminal entry; the final entry has `daysInStage=null`
 *  because we have no subsequent entry to anchor against. */
export function computeStageTimeline(
  history: readonly OpportunityHistoryEntry[],
): StageTimelineEntry[] {
  const nonTerminal = history
    .filter((h) => {
      const stage = (h.stage ?? "").trim();
      return stage !== "" && !TERMINAL_STAGES.has(stage);
    })
    .map((h) => ({
      ...h,
      _date: parseIsoDate(h.created_date),
      _stage: (h.stage ?? "").trim(),
    }))
    // Drop history rows with no usable date — they can't anchor a
    // time-in-stage calculation, and a "stage with no entered-on" line
    // in the UI would be confusing.
    .filter((h) => h._date !== null)
    .sort((a, b) => a._date!.getTime() - b._date!.getTime());

  return nonTerminal.map((h, i) => {
    const next = nonTerminal[i + 1];
    const rawDays = next ? daysBetween(h._date!, next._date!) : null;
    // Clamp clock-skewed entries (next row's timestamp earlier than this
    // row's) to null rather than rendering "-17 days" in the UI. SF
    // history corpus has a small number of these from concurrent-sync
    // race conditions. Pre-PR test-automator HIGH finding.
    const days = rawDays !== null && rawDays < 0 ? null : rawDays;
    return {
      stage: h._stage,
      enteredAt: h._date!.toISOString().slice(0, 10),
      daysInStage: days,
      probability: h.probability,
      amount: h.amount,
    };
  });
}

// ---------------------------------------------------------------------------
// Insight callouts
// ---------------------------------------------------------------------------

/** Days from opp creation to the Closed Lost event. Uses the row's
 *  `close_date` (or, if absent, the most recent history `created_date`).
 *  Null if either anchor is missing/malformed. */
export function computeTimeToLoss(
  createdDate: string | null,
  closeDate: string | null,
): number | null {
  const created = parseIsoDate(createdDate);
  const closed = parseIsoDate(closeDate);
  if (!created || !closed) return null;
  const days = daysBetween(created, closed);
  return days < 0 ? null : days;
}

export interface StalledStageResult {
  stage: string;
  days: number;
}

/** The non-terminal stage where the opp spent the most time. Returns
 *  it only if the dwell exceeds the threshold (default 60 days);
 *  otherwise null. Used to surface "stalled in Discovery for 90 days"
 *  chips.
 *
 *  `closeDate` (optional) is used to compute the dwell of the FINAL
 *  non-terminal stage — by default `computeStageTimeline` leaves the
 *  final entry with `daysInStage=null` because no subsequent entry
 *  anchors it. For lost deals the most common stall pattern IS the
 *  final pre-close stage; passing `closeDate` lets that case surface.
 *  Pre-PR code-reviewer MED finding. */
export function computeStalledStage(
  timeline: readonly StageTimelineEntry[],
  thresholdDays: number = STALLED_THRESHOLD_DAYS,
  closeDate: string | null = null,
): StalledStageResult | null {
  const closed = parseIsoDate(closeDate);
  let worst: StalledStageResult | null = null;
  for (let i = 0; i < timeline.length; i++) {
    const t = timeline[i];
    let dwell = t.daysInStage;
    // If this is the final entry AND we have a close date, fall back to
    // (closeDate - enteredAt) for the dwell.
    if (dwell === null && i === timeline.length - 1 && closed !== null) {
      const entered = parseIsoDate(t.enteredAt);
      if (entered !== null) {
        const finalDwell = daysBetween(entered, closed);
        // Clamp negative (close before entered → SF data bug) to null.
        if (finalDwell >= 0) dwell = finalDwell;
      }
    }
    if (dwell !== null && dwell > thresholdDays) {
      if (worst === null || dwell > worst.days) {
        worst = { stage: t.stage, days: dwell };
      }
    }
  }
  return worst;
}

export interface AbruptLossResult {
  peakStage: string;
  peakProbability: number;
  daysFromPeakToLoss: number;
}

/** "Looked like a win, then suddenly lost" pattern. Triggered when ANY
 *  non-terminal history entry within `thresholdDays` (default 7) of the
 *  close-lost date carried a probability of at least `minPeakProbability`
 *  (default 70%). The 70% floor stops sub-qualification noise from
 *  triggering.
 *
 *  Algorithm — window-first: iterate only entries that fall within the
 *  [0, thresholdDays] window before close; pick the highest-prob entry
 *  from that subset. Pre-PR code-reviewer + test-automator HIGH:
 *  picking the global max first and THEN testing the window misses two
 *  real cases:
 *    (1) An opp peaked at 95% in June (out of window) and again at 80%
 *        3 days before close → the in-window signal is the real "abrupt
 *        loss" event but global-max picks the 95% entry and discards
 *        because it's out of window.
 *    (2) Two in-window entries at the same probability (90% in Pursue
 *        AND Final Negotiation) — global-max picked the first by
 *        iteration order; with window-first + tie-break-on-date we
 *        pick the entry CLOSER to close (more representative).
 *    (3) An entry AFTER closeDate (SF data bug — stage transition logged
 *        post-close) would previously slip into the global-max
 *        comparison; window-first rejects it via gap<0 BEFORE comparing
 *        probabilities. */
export function computeAbruptLoss(
  timeline: readonly StageTimelineEntry[],
  closeDate: string | null,
  thresholdDays: number = ABRUPT_THRESHOLD_DAYS,
  minPeakProbability: number = ABRUPT_PROB_FLOOR,
): AbruptLossResult | null {
  const closed = parseIsoDate(closeDate);
  if (!closed) return null;
  let bestPeak: { stage: string; prob: number; date: Date; gap: number } | null = null;
  for (const t of timeline) {
    const p = t.probability;
    const d = parseIsoDate(t.enteredAt);
    if (p === null || !Number.isFinite(p) || p < minPeakProbability || !d) continue;
    const gap = daysBetween(d, closed);
    // Window check FIRST — out-of-window entries can't be the signal.
    if (gap < 0 || gap > thresholdDays) continue;
    // Within window: pick the highest probability; tie-break on the
    // entry CLOSER to close (smaller gap = more representative of the
    // "looked like a win moments before losing" moment).
    if (
      bestPeak === null ||
      p > bestPeak.prob ||
      (p === bestPeak.prob && gap < bestPeak.gap)
    ) {
      bestPeak = { stage: t.stage, prob: p, date: d, gap };
    }
  }
  if (!bestPeak) return null;
  return {
    peakStage: bestPeak.stage,
    peakProbability: bestPeak.prob,
    daysFromPeakToLoss: bestPeak.gap,
  };
}

export interface RarityResult {
  /** How many closed-lost opps in the corpus share this peak stage. */
  matches: number;
  /** Total closed-lost opps used as the denominator (only those with a
   *  non-null `pre_close_max_stage`). */
  cohortSize: number;
  /** Percentage matches / cohortSize, rounded to one decimal. */
  percentage: number;
}

/** Cross-corpus rarity of a peak-stage outcome among closed-lost opps.
 *  E.g. "this deal reached Final Negotiation; only 8% of closed-lost
 *  deals reached that stage". Uses the global bundle so it works
 *  without a new BE endpoint.
 *
 *  Returns null when:
 *  - peakStage is null (current opp has no history) — no benchmark to
 *    cite
 *  - cohort is empty (corpus has no closed-lost opps with aggregates) —
 *    denominator would be 0
 */
export function computeStagePeakRarity(
  bundleOpps: readonly OpportunityRow[],
  peakStage: string | null,
): RarityResult | null {
  if (!peakStage) return null;
  const trimmed = peakStage.trim();
  if (!trimmed) return null;
  let matches = 0;
  let cohortSize = 0;
  for (const o of bundleOpps) {
    // Closed-lost is the cohort: `is_closed === true && is_won === false`
    // The bundle row carries both as plain booleans.
    if (o.is_closed !== true || o.is_won !== false) continue;
    if (typeof o.pre_close_max_stage !== "string") continue;
    cohortSize += 1;
    if (o.pre_close_max_stage.trim() === trimmed) matches += 1;
  }
  if (cohortSize === 0) return null;
  return {
    matches,
    cohortSize,
    percentage: Math.round((matches / cohortSize) * 1000) / 10,
  };
}
